import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Search } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["new", "in_progress", "resolved", "archived"];
const KINDS = [{ id: "contact", label: "Contact" }, { id: "join", label: "Joining" }, { id: "sponsor", label: "Sponsorship" }];

function EnquiryTable({ kind }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/enquiries/${kind}`, { params: { status, q } });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, status]);

  const setRowStatus = async (id, s) => { await api.put(`/admin/enquiries/${kind}/${id}`, { status: s }); toast.success("Updated"); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this enquiry?")) return; await api.delete(`/admin/enquiries/${kind}/${id}`); load(); };
  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/enquiries/${kind}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${kind}_enquiries.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <input placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="w-full rounded-lg border border-slate-600 bg-[#0C131F] py-2 pl-9 pr-3 text-sm text-white" data-testid={`enquiry-search-${kind}`} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 rounded-lg border border-slate-600 bg-[#0C131F] text-sm text-white" data-testid={`enquiry-status-filter-${kind}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={exportCsv} data-testid={`enquiry-export-${kind}`} className="inline-flex items-center gap-2 rounded-lg bg-slate-700 px-4 py-2 text-sm font-bold text-white hover:bg-slate-600"><Download className="h-4 w-4" /> CSV</button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#0C131F] text-xs uppercase text-slate-400"><tr><th className="p-3">From</th><th className="p-3">Details</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-700/60 align-top" data-testid={`enquiry-row-${r.id}`}>
                <td className="p-3"><div className="font-semibold text-white">{r.name || r.parent_name || r.contact_name || r.business_name}</div><div className="text-xs text-slate-400">{r.email}</div><div className="text-xs text-slate-400">{r.phone}</div></td>
                <td className="p-3 text-slate-300">{r.category && <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-bold uppercase">{r.category}</span>}{r.age_group && <span className="ml-1 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-bold">{r.age_group}</span>}{r.child_name && <div className="text-xs">Child: {r.child_name}</div>}<div className="mt-1 max-w-sm text-xs">{r.message}</div></td>
                <td className="p-3">
                  <Select value={r.status} onValueChange={(s) => setRowStatus(r.id, s)}>
                    <SelectTrigger className="w-32 rounded border border-slate-600 bg-[#0C131F] text-xs text-white" data-testid={`enquiry-set-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3"><button onClick={() => remove(r.id)} className="rounded bg-slate-700 p-1.5 hover:bg-red-600"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-500">No enquiries.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function EnquiriesManager() {
  return (
    <div>
      <h1 className="font-heading text-2xl font-extrabold uppercase text-white">Enquiries</h1>
      <p className="text-sm text-slate-400">View, filter, update and export enquiries.</p>
      <Tabs defaultValue="contact" className="mt-6">
        <TabsList className="bg-[#0C131F]">{KINDS.map((k) => <TabsTrigger key={k.id} value={k.id} data-testid={`enquiry-tab-${k.id}`}>{k.label}</TabsTrigger>)}</TabsList>
        {KINDS.map((k) => <TabsContent key={k.id} value={k.id} className="mt-4"><EnquiryTable kind={k.id} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
